import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PROFILE_FORM_STYLES } from "@/components/Forms/profile-form-styles";
import { cn } from "@/lib/utils";

/**
 * Confirm destroying an uploaded file.
 *
 * The counterpart to `RemoveDocumentDialog`, and its opposite: that one takes a
 * document off a credential and says plainly that the file survives, this one
 * ends the file. The two sit a click apart in the same screens, so each has to
 * be unmistakable about which of the two things is about to happen — and this
 * is the one that cannot be undone.
 *
 * It replaced a bare "Dismiss" that fired immediately. That button read as
 * clearing a message rather than acting on the file, which is the reading that
 * gets a file destroyed by someone who thought they were tidying their queue.
 */
export function DeleteUploadDialog({
  fileLabel,
  isDeleting,
  onConfirm,
  onCancel,
  appearance = "default",
  canKeepFile = true,
}: {
  /** What the reader has been calling this file — its title on the row they
   * clicked, so the dialog names the same thing they were looking at. */
  fileLabel: string;
  isDeleting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  appearance?: "default" | "profile";
  canKeepFile?: boolean;
}) {
  const isProfile = appearance === "profile";
  return (
    <AlertDialog open onOpenChange={(open) => !open && onCancel()}>
      <AlertDialogContent
        className={isProfile ? PROFILE_FORM_STYLES.confirmation : undefined}
      >
        <AlertDialogHeader className={isProfile ? "text-left" : undefined}>
          <AlertDialogTitle
            className={isProfile ? PROFILE_FORM_STYLES.title : undefined}
          >
            Delete this file?
          </AlertDialogTitle>
          <AlertDialogDescription
            className={isProfile ? PROFILE_FORM_STYLES.description : undefined}
          >
            <span className="font-medium">{fileLabel}</span> will be permanently
            deleted from this record and from storage. This cannot be undone.
            {canKeepFile && (
              <>
                <br />
                <br />
                To keep the file instead, close this and file it against a
                credential.
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter
          className={isProfile ? "mt-2 gap-3 sm:space-x-0" : undefined}
        >
          <AlertDialogCancel
            className={
              isProfile
                ? cn(PROFILE_FORM_STYLES.secondaryButton, "mt-0")
                : undefined
            }
            disabled={isDeleting}
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={isDeleting}
            onClick={onConfirm}
            className={
              isProfile
                ? PROFILE_FORM_STYLES.dangerButton
                : "bg-red-600 text-white hover:bg-red-700 focus:ring-red-600"
            }
          >
            {isDeleting ? "Deleting…" : "Delete file"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
